/**
 * FERRAMENTAS LIGÁVEIS E VÍNCULO DO COMPRADOR (tarefa 3C, decisão do gestor, 06/10/2026).
 *
 * O app abre «só pesagem»: ficha → lotes → porteira → pesagem → romaneio. O
 * resto o gestor liga em Ajustes › Ferramentas, para o app não virar «caos
 * administrativo» para quem só quer pesar. Desligar SÓ ESCONDE na tela: nada
 * se apaga, nada muda no banco, e os dados continuam sincronizando.
 *
 * A escolha mora na conta (`contas.ferramentas`, migração 011) e desce no
 * `GET /api/eu`, guardada no aparelho como o resto do «eu» (funciona sem sinal).
 *
 * Sem tela aqui: só a regra, testada em testes/ferramentas.mjs.
 */

export type NomeDaFerramenta = 'comissoes' | 'fechamento' | 'extrato' | 'catira' | 'notas' | 'relatorio'
export type Ferramentas = Partial<Record<NomeDaFerramenta, boolean>>

/** A ordem e as frases da tela Ajustes › Ferramentas. */
export const FERRAMENTAS: { nome: NomeDaFerramenta; titulo: string; frase: string }[] = [
  { nome: 'comissoes', titulo: 'Comissões e pagamentos',
    frase: 'Comissão do comprador (você paga) e comissão do cliente (você recebe), os pagamentos e recebimentos efetivados com comprovante e a tela Contas (a pagar e a receber). A condição combinada (forma e parcelas) aparece sempre.' },
  { nome: 'fechamento', titulo: 'Fechamento para o escritório',
    frase: 'A aba Custo da ficha: o que pagar e para quem, custo por cabeça e por arroba, e a etiqueta «custo pendente».' },
  { nome: 'extrato', titulo: 'Extrato e acerto',
    frase: 'Despesas de viagem e vales de cada comprador, e o acerto por período (botão 🧾 da barra de baixo).' },
  { nome: 'catira', titulo: 'Meu estoque',
    frase: 'O estoque do seu gado por contagem e custo médio: a entrada no topo das Fichas e a marca «Entra no meu estoque» na aba Custo da ficha encerrada.' },
  { nome: 'notas', titulo: 'Notas fiscais',
    frase: 'Registrar as notas (XML, câmera, PDF ou foto) e ligá-las às fichas e despesas. Nunca emite nota.' },
  { nome: 'relatorio', titulo: 'Relatório de receitas e despesas',
    frase: 'Tabela de receitas e despesas por período para o contador e o imposto de renda, em Excel (entrada no topo das Fichas). Só o gestor e a Consulta veem: tem as comissões.' },
]

export const NOMES_DAS_FERRAMENTAS: NomeDaFerramenta[] = FERRAMENTAS.map((f) => f.nome)

/**
 * A ferramenta está ligada nesta conta?
 *
 * - Conta SEM o campo (servidor antes da migração 011, ou «eu» guardado no
 *   aparelho por uma versão antiga): TUDO LIGADO — é o jeito que o gestor já
 *   usa, e o app não pode sumir com tela de quem já trabalha nela.
 * - Conta COM o campo: vale o que está lá; ferramenta que não aparece está
 *   desligada (a conta nova nasce com tudo `false`, explícito, pelo banco).
 */
export function ferramentaLigada(conta: { ferramentas?: Ferramentas | null } | null | undefined, nome: NomeDaFerramenta): boolean {
  const f = conta?.ferramentas
  if (!f || typeof f !== 'object' || Array.isArray(f)) return true
  return f[nome] === true
}

/** O objeto completo, só com as ferramentas conhecidas e só verdadeiro/falso — é o que o servidor grava. */
export function normalizarFerramentas(bruto: unknown): Record<NomeDaFerramenta, boolean> {
  const b = bruto && typeof bruto === 'object' && !Array.isArray(bruto) ? (bruto as Record<string, unknown>) : {}
  return Object.fromEntries(NOMES_DAS_FERRAMENTAS.map((n) => [n, b[n] === true])) as Record<NomeDaFerramenta, boolean>
}

/* ---------------------------------------------------------------------------
 * Vínculo do comprador → padrão de «por conta de quem» da despesa de viagem
 * ------------------------------------------------------------------------- */

export type Vinculo = 'contratado' | 'comissionado'

export const VINCULOS: [Vinculo, string, string][] = [
  ['contratado', 'Contratado', 'salário mensal — a despesa de viagem é da empresa, que devolve no acerto'],
  ['comissionado', 'Comissionado', 'só comissão — a despesa de viagem é dele, fica só como registro'],
]

/**
 * O PADRÃO do formulário de despesa nova (decisão do gestor, 06/10/2026):
 *  - contratado   → 'gestor'     (por conta da empresa, entra no acerto);
 *  - comissionado → 'comprador'  (por conta dele, só registro);
 *  - sem vínculo  → null         (o app pergunta na hora, sem escolher por ele).
 * É só o padrão: cada despesa continua podendo ser trocada.
 */
export function porContaPadrao(vinculo: Vinculo | null | undefined): 'gestor' | 'comprador' | null {
  if (vinculo === 'contratado') return 'gestor'
  if (vinculo === 'comissionado') return 'comprador'
  return null
}

export function lerVinculo(v: unknown): Vinculo | null {
  return v === 'contratado' || v === 'comissionado' ? v : null
}
